import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface Post {
  id: string;
  author: string;
  content: string;
  time: string;
  likes: number;
}

const fakePosts: Post[] = [
  { id: 'fake-1', author: 'Maria S.', content: '¡El protocolo de 21 días está cambiando mi vida! Ya me siento mucho menos hinchada.', time: 'Hoy', likes: 12 },
  { id: 'fake-2', author: 'Ana P.', content: 'Conseguí beber 2 litros de agua hoy. ¡Pequeñas victorias! 💧', time: 'Ayer', likes: 8 },
  { id: 'fake-3', author: 'Lucia M.', content: 'Ayer tuve un desliz y comí demasiada azúcar, pero hoy volví al foco. La consistencia es la clave.', time: 'Hace 2 días', likes: 15 },
];

const Community = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [userName, setUserName] = useState('Usuario/a');
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [hasPostedToday, setHasPostedToday] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());

  useEffect(() => {
    const savedLikes = localStorage.getItem('liked_community_posts');
    if (savedLikes) {
      setLikedPosts(new Set(JSON.parse(savedLikes)));
    }
  }, []);

  useEffect(() => {
    const fetchCommunityData = async () => {
      console.log("DEBUG COMMUNITY: currentUser state", user);
      if (!user || !user.id) {
        console.log("DEBUG COMMUNITY: No user or user.id, skipping fetch.");
        return;
      }
      
      try {
        // Fetch posts and author data via specialized proxy
        const resPosts = await fetch('/api/community/posts');
        const { data: postsData, error } = await resPosts.json();
          
        if (error) {
          console.error("Error fetching posts:", error);
        }

        if (postsData && postsData.length > 0) {
          const todayStr = new Date().toLocaleDateString();
          const userPostToday = postsData.find((p: any) => 
            p.user_id === user.id && new Date(p.created_at).toLocaleDateString() === todayStr
          );
          setHasPostedToday(!!userPostToday);

          const formattedPosts = postsData.map((p: any) => ({
            id: p.id,
            author: p.user_profiles?.full_name || 'Usuario',
            avatarUrl: p.user_profiles?.avatar_url,
            content: p.content,
            time: new Date(p.created_at).toLocaleDateString(),
            likes: p.likes,
          }));
          setPosts([...formattedPosts, ...fakePosts]);
        } else {
          setPosts(fakePosts);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCommunityData();
  }, [user]);

  const handlePost = async () => {
    if (!newPostContent.trim() || !user) return;
    setPosting(true);

    try {
      const res = await fetch('/api/community/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newPostContent
        })
      });

      const { data, error } = await res.json();
    
    if (error) {
      console.error("Error posting:", error);
      toast({ title: "Error al publicar", description: "Es posible que la tabla no esté configurada o haya un problema de conexión. Se agregó de forma local.", variant: "destructive" });
      
      // Fallback: add optimistically so the user sees it in the UI
      const optimisticPost: Post = {
        id: `optimistic-${Date.now()}`,
        author: userName,
        content: newPostContent,
        time: new Date().toLocaleDateString(),
        likes: 0
      };
      setPosts([optimisticPost, ...posts]);
      setNewPostContent('');
      setPosting(false);
      return;
    }

    if (data) {
      const newPost: Post = {
        id: data.id,
        author: userName,
        content: data.content,
        time: new Date(data.created_at).toLocaleDateString(),
        likes: data.likes
      };
      setPosts([newPost, ...posts]);
      setHasPostedToday(true);
      toast({ title: "¡Publicado!", description: "Tu mensaje se compartió con la comunidad." });
    }
    
    } catch (err: any) {
      console.error("Error posting:", err);
      toast({ title: "Error al publicar", description: "Ocurrió un error inesperado.", variant: "destructive" });
    } finally {
      setNewPostContent('');
      setPosting(false);
    }
  };


  const handleLike = async (id: string) => {
    if (likedPosts.has(id)) return;
    
    // Marcar como curtido localmente
    const newLikedPosts = new Set(likedPosts);
    newLikedPosts.add(id);
    setLikedPosts(newLikedPosts);
    localStorage.setItem('liked_community_posts', JSON.stringify(Array.from(newLikedPosts)));

    const postToLike = posts.find(p => p.id === id);
    if (!postToLike) return;
    
    const newLikes = postToLike.likes + 1;
    
    // Optimistic update
    setPosts(posts.map(post => 
      post.id === id ? { ...post, likes: newLikes } : post
    ));
    
    try {
      await fetch('/api/community/like', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: id,
          likes: newLikes
        })
      });
    } catch (err) {
      console.error("Error updating likes:", err);
    }

  };

  return (
    <div className="min-h-screen bg-background flex flex-col animate-fade-in">
      <div className="sticky top-0 bg-background/80 backdrop-blur-md z-10 px-4 py-4 border-b">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app/perfil')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            ← Volver
          </button>
          <h1 className="text-xl font-heading font-bold flex-1 text-center pr-8">🌟 Comunidad</h1>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-8 max-w-xl mx-auto w-full space-y-6">
        {/* Create Post */}
        <div className="bg-card rounded-2xl p-4 shadow-card">
          <Textarea 
            placeholder={hasPostedToday ? "Ya compartiste tu progreso hoy. ¡Vuelve mañana!" : "¿Cómo te sientes hoy? Comparte tu progreso..."} 
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            className="border-none resize-none focus-visible:ring-0 px-0 bg-transparent"
            rows={3}
            disabled={posting || hasPostedToday}
          />
          <div className="flex justify-end mt-2 pt-2 border-t">
            <Button size="sm" className="rounded-full" onClick={handlePost} disabled={!newPostContent.trim() || posting || hasPostedToday}>
              {posting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} 
              Publicar
            </Button>
          </div>
        </div>

        {/* Feed */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 text-primary animate-spin" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground bg-card rounded-2xl">
              ¡Aún no hay publicaciones! Sé el primero en compartir tu progreso.
            </div>
          ) : (
            posts.map(post => (
              <div key={post.id} className="bg-card rounded-2xl p-5 shadow-card space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {post.avatarUrl ? (
                      <img src={post.avatarUrl} alt={post.author} className="w-10 h-10 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {post.author.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-sm">{post.author}</p>
                      <p className="text-xs text-muted-foreground">{post.time}</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed">{post.content}</p>
                <div className="flex items-center gap-4 pt-2">
                  <button 
                    onClick={() => handleLike(post.id)} 
                    disabled={likedPosts.has(post.id) || post.id.startsWith('fake-')}
                    className={`flex items-center gap-1.5 text-xs transition-colors ${
                      likedPosts.has(post.id) ? 'text-wellness-peach' : 'text-muted-foreground hover:text-wellness-peach'
                    }`}
                  >
                    <Heart className={`h-4 w-4 ${likedPosts.has(post.id) ? 'fill-wellness-peach' : ''}`} /> 
                    {post.likes}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Community;
